/**
 * Every plugin page in the CMS: the file it lives in, and how the URL's `params` segment
 * reaches it. This is the one list. The router's plugin route (`router/components/PluginPage.js`)
 * looks a page up here by `<pluginName>#<pageName>`, loads its file and passes it its props.
 *
 * **`load`** is the page's file, as `() => import(...)`. A page loads the first time it
 * opens, not with the app, and its code starts downloading as soon as the router knows which
 * page it is opening, or when the pointer is over its link in the rail (`router/pageLoading.js`).
 * Several entries can load the same file: `meets` has three pages that differ only by a constant.
 *
 * The rest says where the page's props come from. It replaces the 134 Angular bridge templates
 * that used to live at `plugins/<name>/cms/web/<page>.html`, which between them used four
 * binding forms:
 *
 *   `plugin.<key>`    the plugin's own config value. Implicit: every key on the plugin object
 *                     is passed through by name, so `page-title="plugin.pageTitle"` needs no
 *                     entry. `fromPlugin` covers the one page that renamed a key.
 *   `params`          the whole `:params` URL segment, under a page-specific prop name.
 *                     Written here as a one-element `params` list plus `wholeParams: true`.
 *                     **The flag is load-bearing.** Four pages bound a single
 *                     `splitParams[0]` instead, which looks identical but splits on commas,
 *                     and those must keep splitting or the first caller to pass a second
 *                     argument hands the page "12,34" as its id.
 *   `splitParams[n]`  `params` split on commas, positionally. Only the template recorded which
 *                     position became which prop, so the order is written here.
 *   a string literal  three `meets` pages that differ only by a constant. Written as `props`.
 *
 * **The order in `params` is a contract with the callers**, the 104 React files that dispatch
 * `navigateToPluginPage`, `navigateToChildPage` or `navigateToParentPage` with a positional
 * argument list. Four pages bind positions in an order that differs from the order their
 * component declares its props in (the three publish pages and `itrs-session-create-page`);
 * the order below is the template's, which is what the callers were written against. Do not
 * "correct" it.
 *
 * Keys are `<pluginName>#<pageName>`, the same `#` separator a tenant's `sideMenu` uses, and
 * `pluginName` may contain slashes (`custom-plugins/ubc/dues-payment`). The URL escapes those
 * as `~2F` (`tildeEncode` in `router/definitions.js`), exactly as ui-router did, so links are
 * unchanged.
 *
 * It was generated from the templates by `.harness/genPluginPages.js`, with a separate
 * name-to-component registry, `componentRegistry.js`, which the `load` entries replaced.
 * `__tests__/pluginPages.test.js` checks each entry's file and props, and
 * `__tests__/pagesLoadOnDemand.test.js` that every page loads on demand.
 */
const pluginPages = {
  'achievements#achievementCertEditorPage': {
    load: () => import('./achievement/components/AchievementCertEditorPage'),
  },
  'achievements#achievementEditorPage': {
    load: () => import('./achievement/components/AchievementEditorPage'),
    wholeParams: true,
    params: ['achievementId'],
  },
  'achievements#achievementFieldEditorPage': {
    load: () => import('./achievement/components/AchievementFieldEditorPage'),
  },
  'achievements#achievementInstanceEditorPage': {
    load: () => import('./achievement/components/AchievementInstanceEditorPage'),
    params: ['achievementId', 'achievementInstanceId'],
  },
  'achievements#achievementInstancesPage': {
    load: () => import('./achievement/components/AchievementInstancesPage'),
    wholeParams: true,
    params: ['achievementId'],
  },
  'achievements#achievementTemplateManagerPage': {
    load: () => import('./achievement/components/AchievementTemplateManagerPage'),
  },
  'achievements#default': { load: () => import('./achievement/components/AchievementsListPage') },
  'attendance-tracking#attendanceEventAttendeePage': {
    load: () => import('./attendance-tracking/components/AttendanceEventAttendeePage'),
    params: ['attendanceEventId'],
  },
  'attendance-tracking#attendanceEventEditorPage': {
    load: () => import('./attendance-tracking/components/AttendanceEventEditorPage'),
    wholeParams: true,
    params: ['attendanceEventId'],
  },
  'attendance-tracking#attendanceEventGroupEditorPage': {
    load: () => import('./attendance-tracking/components/AttendanceEventGroupEditorPage'),
    wholeParams: true,
    params: ['id'],
  },
  'attendance-tracking#attendanceEventGroupsPage': {
    load: () => import('./attendance-tracking/components/AttendanceEventGroupsPage'),
  },
  'attendance-tracking#default': {
    load: () => import('./attendance-tracking/components/AttendanceEventPage'),
  },
  'books#default': {
    load: () => import('./books/components/BookListPage'),
    fromPlugin: { pluginName: 'name' },
  },
  'calendar-events#calendarEventsEditorPage': {
    load: () => import('./calendar-events/components/CalendarEventsEditorPage'),
    wholeParams: true,
    params: ['calendarEventId'],
  },
  'calendar-events#default': {
    load: () => import('./calendar-events/components/CalendarEventsListPage'),
  },
  'calendar-list#default': { load: () => import('./calendar-list/components/CalendarListPage') },
  'cohorts#cohortEditor': {
    load: () => import('./cohort/components/CohortEditorPage'),
    wholeParams: true,
    params: ['cohortId'],
  },
  'cohorts#cohortMatchesPage': {
    load: () => import('./cohort/components/CohortMatchesPage'),
    params: ['cohortId', 'credentialCode'],
  },
  'cohorts#default': { load: () => import('./cohort/components/CohortListPage') },
  'collections#collectionEditor': {
    load: () => import('./collection/components/CollectionEditorPage'),
    wholeParams: true,
    params: ['collectionId'],
  },
  'collections#collectionEditorPage': {
    load: () => import('./collection/components/CollectionEditorPage'),
    wholeParams: true,
    params: ['collectionId'],
  },
  'collections#collectionItemEditor': {
    load: () => import('./collection/components/CollectionItemEditorPage'),
    params: ['collectionId', 'itemId'],
  },
  'collections#collectionItemEditorPage': {
    load: () => import('./collection/components/CollectionItemEditorPage'),
    params: ['collectionId', 'itemId'],
  },
  'collections#collectionItemPublishPage': {
    load: () => import('./collection/components/CollectionItemPublishPage'),
    params: ['collectionId', 'returnPageId', 'returnPageName', 'returnPageLabel', 'successGrowlId'],
  },
  'collections#collectionSettingsPage': {
    load: () => import('./collection/components/CollectionSettingsPage'),
    wholeParams: true,
    params: ['collectionId'],
  },
  'collections#default': { load: () => import('./collection/components/CollectionListPage') },
  'course-manager#closePreview': {
    load: () => import('./course-manager/components/CourseClosePreview'),
  },
  'course-manager#default': { load: () => import('./course-manager/components/CoursesListPage') },
  'custom-pages#customPageEditor': {
    load: () => import('./custom-page/components/CustomPageEditor'),
    wholeParams: true,
    params: ['customPageId'],
  },
  'custom-pages#default': { load: () => import('./custom-page/components/CustomPageList') },
  'custom-plugins/iuoe/itrs#default': { load: () => import('./itrs/components/ItrsDashboardPage') },
  'custom-plugins/iuoe/itrs#itrs-course-create-page': {
    load: () => import('./itrs/components/ItrsCourseCreatePage'),
    wholeParams: true,
    params: ['courseId'],
  },
  'custom-plugins/iuoe/itrs#itrs-course-list-page': {
    load: () => import('./itrs/components/ItrsCourseListPage'),
  },
  'custom-plugins/iuoe/itrs#itrs-course-view-page': {
    load: () => import('./itrs/components/ItrsCourseViewPage'),
    params: ['courseId', 'returnTo'],
  },
  'custom-plugins/iuoe/itrs#itrs-email-preference-category-program-page': {
    load: () => import('./itrs/components/ItrsEmailPreferenceCategoryProgramPage'),
    wholeParams: true,
    params: ['typeKey'],
  },
  'custom-plugins/iuoe/itrs#itrs-email-preferences-page': {
    load: () => import('./itrs/components/ItrsEmailPreferencesPage'),
  },
  'custom-plugins/iuoe/itrs#itrs-enrollment-details-page': {
    load: () => import('./itrs/components/ItrsEnrollmentDetailsPage/index'),
    params: ['sessionId', 'enrollmentId', 'activeTab', 'returnTo'],
  },
  'custom-plugins/iuoe/itrs#itrs-mail-log-item-page': {
    load: () => import('./itrs/components/ItrsMailLogItemPage'),
    wholeParams: true,
    params: ['itemId'],
  },
  'custom-plugins/iuoe/itrs#itrs-mail-log-page': {
    load: () => import('./itrs/components/ItrsMailLogPage'),
  },
  'custom-plugins/iuoe/itrs#itrs-reports-page': {
    load: () => import('./itrs/components/ItrsReportsPage'),
  },
  'custom-plugins/iuoe/itrs#itrs-session-create-page': {
    load: () => import('./itrs/components/ItrsSessionCreatePage'),
    params: ['courseId', 'sessionId'],
  },
  'custom-plugins/iuoe/itrs#itrs-session-view-page': {
    load: () => import('./itrs/components/ItrsSessionViewPage'),
    params: ['sessionId', 'returnTo'],
  },
  'custom-plugins/iuoe/itrs#itrs-travel-confirmation-page': {
    load: () => import('./itrs/components/travel/ItrsTravelConfirmationPage'),
    params: ['travelRequestId', 'sessionId', 'enrollmentId', 'returnTo'],
  },
  'custom-plugins/iuoe/itrs#itrs-travel-edit-page': {
    load: () => import('./itrs/components/travel/ItrsTravelEditPage'),
    params: ['travelRequestId', 'sessionId', 'enrollmentId', 'returnTo'],
  },
  'custom-plugins/iuoe/itrs#itrs-travel-requests-page': {
    load: () => import('./itrs/components/travel/ItrsTravelRequestsPage'),
  },
  'custom-plugins/ubc/dues-payment#default': {
    load: () => import('./ubc-dues/components/DuesPaymentPage'),
  },
  'custom-plugins/ubc/dues-payment#paymentLocalEditor': {
    load: () => import('./ubc-dues/components/PaymentLocalEditorPage'),
    wholeParams: true,
    params: ['params'],
  },
  'custom-plugins/ubc/ubc-auth#default': {
    load: () => import('./ubc-auth/components/UbcAuthSettingsPage'),
  },
  'directories#default': { load: () => import('./directories/components/DirectoriesListPage') },
  'directories#directoryBuilder': {
    load: () => import('./directories/components/DirectoryBuilderPage'),
    wholeParams: true,
    params: ['params'],
  },
  'directories#directoryMigrationsManager': {
    load: () => import('./directories/components/DirectoryMigrationsManagerPage'),
  },
  'directories#directoryV1DataTransfer': {
    load: () => import('./directories/components/DirectoryV1DataTransferPage'),
  },
  'directories#divisions': {
    load: () => import('./directories/components/DivisionsPage'),
    wholeParams: true,
    params: ['params'],
  },
  'directories#entries': {
    load: () => import('./directories/components/EntriesPage'),
    wholeParams: true,
    params: ['params'],
  },
  'directories#entryForm': {
    load: () => import('./directories/components/EntryFormPage'),
    wholeParams: true,
    params: ['params'],
  },
  'forms#default': { load: () => import('./forms/components/FormsListPage') },
  'forms#formBuilderPage': {
    load: () => import('./forms/components/FormBuilderPage'),
    wholeParams: true,
    params: ['formId'],
  },
  'forms#formInstanceEditorPage': {
    load: () => import('./forms/components/FormInstanceEditorPage'),
    params: ['formId', 'formInstanceId'],
  },
  'forms#formInstancesPage': {
    load: () => import('./forms/components/FormInstancesPage'),
    wholeParams: true,
    params: ['formId'],
  },
  'general-files#default': { load: () => import('./files/components/GeneralFilesListPage') },
  'general-files#generalFilesEditorPage': {
    load: () => import('./files/components/GeneralFilesEditorPage'),
    wholeParams: true,
    params: ['generalFilesId'],
  },
  'lead-generation#default': {
    load: () => import('./lead-generation/components/LeadGenEventPage'),
  },
  'lead-generation#leadGenAttendeesPage': {
    load: () => import('./lead-generation/components/LeadGenAttendeesPage'),
    params: ['leadGenId', 'eventName', 'resultType', 'ingestType'],
  },
  'lead-generation#leadGenEventCustomFieldsPage': {
    load: () => import('./lead-generation/components/LeadGenEventCustomFieldsPage'),
    wholeParams: true,
    params: ['leadGenId'],
  },
  'lead-generation#leadGenEventEditorPage': {
    load: () => import('./lead-generation/components/LeadGenEventEditorPage'),
    wholeParams: true,
    params: ['leadGenId'],
  },
  'manage-app-users#appUserCustomFields': {
    load: () => import('./app-users/components/AppUserCustomFields'),
  },
  'manage-app-users#appUserEditorPage': {
    load: () => import('./app-users/components/AppUserEditorPage'),
    params: ['appUserId'],
  },
  'manage-app-users#csvExportEditorPage': {
    load: () => import('./app-users/components/CsvExportEditorPage'),
  },
  'manage-app-users#default': { load: () => import('./app-users/components/AppUserListPage') },
  'manage-assets#appUserEditorPage': {
    load: () => import('./app-users/components/AppUserEditorPage'),
    params: ['appUserId'],
  },
  'manage-assets#csvExportEditorPage': {
    load: () => import('./app-users/components/CsvExportEditorPage'),
  },
  'manage-assets#default': { load: () => import('./manage-assets/pages/ManageAssetListPage') },
  'manage-groups#addGroup': {
    load: () => import('./manage-groups/components/AddGroup'),
    params: ['groupId'],
  },
  'manage-groups#default': {
    load: () => import('./manage-groups/components/ManageGroupsListPage'),
  },
  'manage-groups#manageGroupsCsvExportEditorPage': {
    load: () => import('./manage-groups/components/ManageGroupsCsvExportEditorPage'),
  },
  'manage-groups#manageGroupsEditMembers': {
    load: () => import('./manage-groups/components/ManageGroupsEditMembers'),
    params: ['groupId', 'groupName', 'groupKey', 'groupCanAssign'],
  },
  'meets#broadcast': {
    load: () => import('./meets/components/MeetsListPage'),
    props: { meetType: 'broadcast' },
  },
  'meets#meeting': {
    load: () => import('./meets/components/MeetsListPage'),
    props: { meetType: 'meeting' },
  },
  'meets#meetsEditor': {
    load: () => import('./meets/components/MeetsEditorPage'),
    params: ['meetId', 'meetType'],
  },
  'meets#vod': {
    load: () => import('./meets/components/MeetsListPage'),
    props: { meetType: 'vod' },
  },
  'menu-manager#default': { load: () => import('./menu/components/MenuListPage') },
  'menu-manager#menuEditorPage': {
    load: () => import('./menu/components/MenuEditorPage'),
    wholeParams: true,
    params: ['menuId'],
  },
  'menu-manager#menuItemEditorPage': {
    load: () => import('./menu/components/MenuItemEditorPage'),
    params: ['menuId', 'itemId'],
  },
  'menu-manager#menuItemPublishPage': {
    load: () => import('./menu/components/MenuItemPublishPage'),
    params: ['menuId', 'returnPageId', 'returnPageName', 'returnPageLabel', 'successGrowlId'],
  },
  'menu-navbar#default': {
    load: () => import('./navbar/components/NavbarEditorPage'),
    wholeParams: true,
    params: ['menuId'],
  },
  'menu-navbar#navbarEditorPage': {
    load: () => import('./navbar/components/NavbarEditorPage'),
    wholeParams: true,
    params: ['menuId'],
  },
  'menu-navbar#navbarItemEditorPage': {
    load: () => import('./navbar/components/NavbarItemEditorPage'),
    params: ['menuId', 'itemId'],
  },
  'menu-navbar#navbarItemPublishPage': {
    load: () => import('./navbar/components/NavbarItemPublishPage'),
    params: ['menuId', 'returnPageId', 'returnPageName', 'returnPageLabel', 'successGrowlId'],
  },
  'new-maps#default': { load: () => import('./new-maps/components/MapsListPage') },
  'new-maps#mapsDetailPage': {
    load: () => import('./new-maps/components/MapsDetailPage'),
    wholeParams: true,
    params: ['mapId'],
  },
  'new-maps#mapsKmlEditor': {
    load: () => import('./new-maps/components/MapsKmlEditor'),
    params: ['itemId', 'mapId', 'kmlType'],
  },
  'pdf-manager#default': { load: () => import('./pdf-manager/components/PdfManagerPage') },
  'ptsi#default': { load: () => import('./ptsi/components/PtsiPluginPage') },
  'push#default': { load: () => import('./push/components/PushNotificationListPage') },
  'push#pushNotificationEditorPage': {
    load: () => import('./push/components/PushNotificationEditorPage'),
    params: ['pushNotificationId', 'allowEdit', 'baseNotificationId'],
  },
  'push#pushNotificationsCsvExportPage': {
    load: () => import('./push/components/PushNotificationsCsvExportPage'),
  },
  'qrscanner#default': { load: () => import('./qrscanner/components/QrScannerListPage') },
  'qrscanner#logs': { load: () => import('./qrscanner/components/QrScannerLogs') },
  'qrscanner#qrScannerEditorPage': {
    load: () => import('./qrscanner/components/QrScannerEditorPage'),
    wholeParams: true,
    params: ['qrscannerId'],
  },
  'qrscanner#qrScannerLogExport': {
    load: () => import('./qrscanner/components/QrScannerLogExport'),
    wholeParams: true,
    params: ['exportOptions'],
  },
  'qrscanner#qrScannerTargetEditor': {
    load: () => import('./qrscanner/components/QrScannerTargetEditor'),
    wholeParams: true,
    params: ['targetId'],
  },
  'qrscanner#qrScannerTargetGroupEditor': {
    load: () => import('./qrscanner/components/QrScannerTargetGroupEditor'),
    wholeParams: true,
    params: ['targetGroupId'],
  },
  'qrscanner#qrScannerTargetGroups': {
    load: () => import('./qrscanner/components/QrScannerTargetGroups'),
  },
  'qrscanner#targets': { load: () => import('./qrscanner/components/QrScannerTargets') },
  'quicksight#default': { load: () => import('./quicksight/components/QuicksightReportPage') },
  'quizzes#default': { load: () => import('./quizzes/components/QuizzesListPage') },
  'quizzes#quizBuilderPage': {
    load: () => import('./quizzes/components/QuizBuilderPage'),
    wholeParams: true,
    params: ['quizId'],
  },
  'quizzes#quizInstanceEditorPage': {
    load: () => import('./quizzes/components/QuizInstanceEditorPage'),
    params: ['quizId', 'quizInstanceId'],
  },
  'quizzes#quizInstancesPage': {
    load: () => import('./quizzes/components/QuizInstancesPage'),
    wholeParams: true,
    params: ['quizId'],
  },
  'registrations#default': { load: () => import('./registrations/components/EventsListPage') },
  'registrations#eventEditor': {
    load: () => import('./registrations/components/EventsEditorPage'),
    wholeParams: true,
    params: ['eventId'],
  },
  'registrations#eventsListPage': {
    load: () => import('./registrations/components/EventsListPage'),
  },
  'registrations#registrationEditor': {
    load: () => import('./registrations/components/RegistrationsEditorPage'),
    wholeParams: true,
    params: ['registrationId'],
  },
  'registrations#registrationsListPage': {
    load: () => import('./registrations/components/RegistrationsListPage'),
  },
  'reporting#default': { load: () => import('./reporting/components/ReportingPage') },
  'resource-categories#default': {
    load: () => import('./resource-categories/components/ResourceCategoriesPage'),
  },
  'resource-categories#newsletter': {
    load: () => import('./resource-categories/components/NewslettersPage'),
  },
  'resource-categories#newsletterForm': {
    load: () => import('./resource-categories/components/NewsletterFormPage'),
    wholeParams: true,
    params: ['params'],
  },
  'resource-categories#resourceCategoriesList': {
    load: () => import('./resource-categories/components/CategoryResourcesPage'),
    wholeParams: true,
    params: ['params'],
  },
  'selfregistration#privacy': { load: () => import('./privacy/components/PrivacyPage') },
  'slideshows#default': { load: () => import('./slideshows/components/SlideShowListPage') },
  'surveys#default': { load: () => import('./surveys/components/SurveysListPage') },
  'surveys#surveyInstanceEditorPage': {
    load: () => import('./surveys/components/SurveyInstanceEditorPage'),
    params: ['surveyId', 'surveyInstanceId'],
  },
  'surveys#surveyInstancesPage': {
    load: () => import('./surveys/components/SurveyInstancesPage'),
    wholeParams: true,
    params: ['surveyId'],
  },
  'target-audience#default': {
    load: () => import('./target-audience/components/TargetAudienceListPage'),
  },
  'target-audience#targetAudienceEditorPage': {
    load: () => import('./target-audience/components/TargetAudienceEditorPage'),
    wholeParams: true,
    params: ['targetAudienceId'],
  },
  'template#default': { load: () => import('./template/components/TemplateItemsListPage') },
  'template#templateItemEditorPage': {
    load: () => import('./template/components/TemplateItemsEditorPage'),
    wholeParams: true,
    params: ['templateItemId'],
  },
  'theme#default': { load: () => import('./theme/components/Theme') },
  'url-manager#default': { load: () => import('./url-manager/components/UrlManagerPage') },
  'url-manager#editUrlReactPage': {
    load: () => import('./url-manager/components/ExternalLinkEditorPage'),
    wholeParams: true,
    params: ['params'],
  },
  'workflow#default': { load: () => import('./workflow/components/WorkflowListPage') },
  'workflow#workflow-log-page': {
    load: () => import('./workflow/components/WorkflowLogPage'),
    wholeParams: true,
    params: ['workflowType'],
  },
  'wysiwyg-manager#default': {
    load: () => import('./wysiwyg-manager/components/WysiwygManagerPage'),
  },
  'wysiwyg-manager#pageEditor': {
    load: () => import('./wysiwyg-manager/components/WysiwygPageEditor'),
    wholeParams: true,
    params: ['params'],
  },
};

export default pluginPages;
